import { injectable } from 'inversify';

import {
  WidgetPartition,
  WidgetPartitionCreateOption,
} from '@app/gen/dashboard';

import { TASKS_PARTITIONS, TASKS_WIDGET } from '../../../../constants';
import { TasksOption, TasksOptions } from '../../../../types';

type TasksSide = 'employee' | 'company';

@injectable()
export class TasksPartitionsMapper {
  private groupBySide<T>(
    availablePartitions: WidgetPartitionCreateOption[],
    mapper: (type: WidgetPartition['type'], side: TasksSide) => T,
  ): Record<TasksSide, T[]> {
    const result: Record<TasksSide, T[]> = {
      employee: [],
      company: [],
    };

    availablePartitions.forEach(({ type }) => {
      const config = TASKS_PARTITIONS[type];
      if (!config) return;

      result[config.side].push(mapper(type, config.side));
    });

    return result;
  }

  proccessTasksPartitions(
    availablePartitions: WidgetPartitionCreateOption[],
    requiredPartitions: WidgetPartitionCreateOption['type'][],
  ) {
    const partitionOrder = Object.keys(TASKS_PARTITIONS);

    const sortedPartitions = [...availablePartitions].sort(
      (a, b) => partitionOrder.indexOf(a.type) - partitionOrder.indexOf(b.type),
    );

    const grouped = this.groupBySide(sortedPartitions, (type) => ({
      id: type,
      label: TASKS_PARTITIONS[type]?.label || '',
      disabled: requiredPartitions.some(
        (requiredType) => requiredType === type,
      ),
    }));

    return [
      { ...TASKS_WIDGET.employee, options: grouped.employee },
      { ...TASKS_WIDGET.company, options: grouped.company },
    ];
  }

  proccessActiveTasksPartitions(
    partitions: WidgetPartition[],
    availablePartitions: WidgetPartitionCreateOption[],
  ): TasksOptions {
    const selectedTypes = new Set(partitions.map((p) => p.type));

    const grouped = this.groupBySide<TasksOption>(
      [...partitions, ...availablePartitions],
      (type) => ({
        id: type,
        value: selectedTypes.has(type) ? 'always' : 'direct_only',
      }),
    );

    return {
      employee_tasks: grouped.employee,
      company_tasks: grouped.company,
    };
  }
}
